import type { MessageDescriptor } from 'react-intl';
import { defineMessages } from 'react-intl';

/**
 * Reason codes account-management's AddOrganizationMember prefixes onto its
 * gRPC status message for the invite rejections a client is expected to
 * branch on (ACCOUNT-2597). The prefix is the only part of the error that
 * survives api-gateway-lib's ProtoErrorHandler on the HTTP path, so the
 * ErrorInfo detail carrying the same reason never reaches a browser client —
 * see stepUpProofToken.ts for the same convention on a different RPC.
 */
export const INVITE_ERROR_REASON_CODES = [
  'INVITEE_IN_GRACE_PERIOD',
  'INVITE_ALREADY_EXISTS',
  'INVITEE_DOMAIN_CLAIMED_BY_OTHER_ORG',
  'RESTRICTED_DOMAIN_MISMATCH',
  'TOO_MANY_PENDING_INVITES',
  'TOO_MANY_DAILY_INVITES',
] as const;

export type InviteErrorReasonCode = (typeof INVITE_ERROR_REASON_CODES)[number];

const REASON_CODE_SET: ReadonlySet<string> = new Set(INVITE_ERROR_REASON_CODES);

const REASON_PREFIX_PATTERN = /^([A-Z_]+): /;

/**
 * Matched per line because ApiError joins a multi-error response body with
 * newlines, and as a prefix rather than a substring so a message merely
 * quoting the reason isn't mistaken for a rejection. Unrecognized codes
 * (borderline/internal codes, or none at all) return undefined so callers
 * fall back to the generic error copy.
 */
export function parseInviteErrorReason(error: unknown): InviteErrorReasonCode | undefined {
  if (!(error instanceof Error)) {
    return undefined;
  }

  for (const line of error.message.split('\n')) {
    const code = REASON_PREFIX_PATTERN.exec(line)?.[1];
    if (code != null && REASON_CODE_SET.has(code)) {
      return code as InviteErrorReasonCode;
    }
  }

  return undefined;
}

// Shared toast copy for the reason codes account-management surfaces to the
// client (ACCOUNT-2597). Kept in one place — rather than duplicated per
// tray — so a future copy or backend-code change can't update one tray's
// translation contract and silently miss the other. RESTRICTED_DOMAIN_MISMATCH
// is deliberately excluded — callers handle it as an inline email field error
// instead of a toast (see domainMismatchMessage in utils/constants.ts).
const messages = defineMessages({
  inviteErrorGracePeriod: {
    id: 'Team.inviteTray.inviteErrorGracePeriod',
    defaultMessage:
      "This person's account is still being moved to their organization's managed domain, so they can't be invited yet. Try again later, or contact support if it persists.",
    description:
      'Toast shown when an invite fails because the invitee is mid domain-claim grace period',
  },
  inviteErrorAlreadyExists: {
    id: 'Team.inviteTray.inviteErrorAlreadyExists',
    defaultMessage: 'This person has already been invited or is already a member.',
    description: 'Toast shown when an invite fails because the invitee already has an invite',
  },
  inviteErrorDomainClaimedByOtherOrg: {
    id: 'Team.inviteTray.inviteErrorDomainClaimedByOtherOrg',
    defaultMessage:
      "This email address is managed by another organization and can't be invited here.",
    description:
      "Toast shown when an invite fails because the invitee's email domain is claimed by a different organization",
  },
  inviteErrorTooManyPendingInvites: {
    id: 'Team.inviteTray.inviteErrorTooManyPendingInvites',
    defaultMessage:
      'Your organization has reached its limit for pending invitations. Cancel some pending invites and try again.',
    description:
      'Toast shown when an invite fails because the organization hit its pending-invite cap',
  },
  inviteErrorTooManyDailyInvites: {
    id: 'Team.inviteTray.inviteErrorTooManyDailyInvites',
    defaultMessage:
      "Your organization has reached today's invite limit. Please try again tomorrow.",
    description:
      'Toast shown when an invite fails because the organization hit its daily invite cap',
  },
});

export const INVITE_ERROR_TOAST_MESSAGE_BY_REASON: Partial<
  Record<InviteErrorReasonCode, MessageDescriptor>
> = {
  INVITEE_IN_GRACE_PERIOD: messages.inviteErrorGracePeriod,
  INVITE_ALREADY_EXISTS: messages.inviteErrorAlreadyExists,
  INVITEE_DOMAIN_CLAIMED_BY_OTHER_ORG: messages.inviteErrorDomainClaimedByOtherOrg,
  TOO_MANY_PENDING_INVITES: messages.inviteErrorTooManyPendingInvites,
  TOO_MANY_DAILY_INVITES: messages.inviteErrorTooManyDailyInvites,
};
